import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { OrcamentoDialog, OrcamentoIcon as Icon } from './OrcamentoUI';

const CORES = [['#3182ce', 'Azul'], ['#38a169', 'Verde'], ['#dd6b20', 'Laranja'], ['#e53e3e', 'Vermelho'], ['#805ad5', 'Roxo'], ['#d69e2e', 'Amarelo'], ['#319795', 'Turquesa'], ['#718096', 'Cinzento']];

export default function NovoProcessoManutencao({ tipo, onClose, onCreated }) {
    const [form, setForm] = useState({ posicao: '', cliente_id: '', descricao: '', data_inicio: '', data_fim: '', observacoes: '', cor: tipo === 'pendencia' ? '#e53e3e' : '#805ad5' });
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [retry, setRetry] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const submitting = useRef(false);
    const titulo = tipo === 'pendencia' ? 'Nova pendência' : 'Nova manutenção';
    const atualizar = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));
    useEffect(() => {
        let active = true;
        setLoading(true); setLoadError('');
        api.get('/clientes').then(r => {
            if (active) setClientes(Array.isArray(r.data) ? r.data : []);
        }).catch(() => { if (active) setLoadError('Não foi possível carregar os clientes. Tente novamente.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [retry]);
    const fechar = () => { if (!submitting.current) onClose(); };
    const guardar = async e => {
        e.preventDefault();
        if (submitting.current) return;
        if (!form.posicao.trim() || !form.cliente_id || !form.data_inicio || !form.data_fim) {
            setError('Preencha a posição, o cliente e as datas de início e fim.'); return;
        }
        if (form.data_fim < form.data_inicio) { setError('A data de fim não pode ser anterior à data de início.'); return; }
        submitting.current = true; setSaving(true); setError('');
        try {
            const r = await api.post('/manutencao/obras', { ...form, posicao: form.posicao.trim(), tipo_obra: tipo });
            onCreated(r.data.obra);
        } catch (e) {
            setError(e.response?.data?.error || e.response?.data?.message || 'Não foi possível criar o processo. Os dados foram mantidos para tentar novamente.');
        } finally { submitting.current = false; setSaving(false); }
    };
    return <OrcamentoDialog data-maintenance-dialog="true" onClose={fechar} label={titulo}>
        <form className="modal-ef mnt-modal mnt-create-dialog" onSubmit={guardar} aria-busy={saving}>
            <div className="modal-ef-header">
                <h3>{titulo}</h3>
                <button type="button" className="modal-close" onClick={fechar} disabled={saving} aria-label="Fechar"><Icon name="close" fallback="×" /></button>
            </div>
            <div className="mnt-modal-body">
                <p className="mnt-create-intro">O processo será criado como planeado e ficará disponível na manutenção e no planeamento.</p>
                {loadError && <div role="alert" className="mnt-create-error">{loadError} <button type="button" className="mnt-btn mnt-secondary" onClick={() => setRetry(n => n + 1)}>Tentar novamente</button></div>}
                {!loading && !loadError && !clientes.length && <p role="status">Não há clientes disponíveis. É necessário registar um cliente antes de criar o processo.</p>}
                <fieldset disabled={saving} className="mnt-create-fields">
                    <div className="mnt-create-grid">
                        <div className="modal-field"><label htmlFor="mnt-new-posicao">Posição *</label>
                            <input id="mnt-new-posicao" autoFocus required value={form.posicao} onChange={e => atualizar('posicao', e.target.value)} /></div>
                        <div className="modal-field"><label htmlFor="mnt-new-cliente">Cliente *</label>
                            <select id="mnt-new-cliente" required value={form.cliente_id} disabled={loading || !!loadError} onChange={e => atualizar('cliente_id', e.target.value)}>
                                <option value="">{loading ? 'A carregar clientes…' : 'Selecionar cliente'}</option>
                                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                            </select></div>
                        <div className="modal-field mnt-create-wide"><label htmlFor="mnt-new-descricao">Descrição / local</label>
                            <input id="mnt-new-descricao" value={form.descricao} onChange={e => atualizar('descricao', e.target.value)} /></div>
                        <div className="modal-field"><label htmlFor="mnt-new-inicio">Data de início *</label>
                            <input id="mnt-new-inicio" type="date" required value={form.data_inicio} onChange={e => atualizar('data_inicio', e.target.value)} /></div>
                        <div className="modal-field"><label htmlFor="mnt-new-fim">Data de fim *</label>
                            <input id="mnt-new-fim" type="date" required min={form.data_inicio || undefined} value={form.data_fim} onChange={e => atualizar('data_fim', e.target.value)} /></div>
                        <div className="modal-field mnt-create-wide">
                            <span id="mnt-new-cor-label">Cor</span>
                            <div className="mnt-create-colors" role="group" aria-labelledby="mnt-new-cor-label">
                                {CORES.map(([cor, nome]) => <button key={cor} type="button"
                                    aria-label={nome} title={nome} aria-pressed={form.cor === cor}
                                    style={{ backgroundColor: cor }} onClick={() => atualizar('cor', cor)}>
                                    {form.cor === cor ? '✓' : ''}
                                </button>)}
                            </div>
                        </div>
                        <div className="modal-field mnt-create-wide"><label htmlFor="mnt-new-observacoes">Observações</label>
                            <textarea id="mnt-new-observacoes" rows={3} value={form.observacoes} onChange={e => atualizar('observacoes', e.target.value)} /></div>
                    </div>
                </fieldset>
                {error && <p role="alert" className="mnt-create-error">{error}</p>}
            </div>
            <div className="modal-form-actions">
                <button type="button" className="mnt-btn mnt-secondary" onClick={fechar} disabled={saving}>Cancelar</button>
                <button type="submit" className="mnt-btn mnt-primary" disabled={saving || loading || !!loadError || !clientes.length}>
                    {saving ? 'A criar…' : tipo === 'pendencia' ? 'Criar pendência' : 'Criar manutenção'}
                </button>
            </div>
        </form>
    </OrcamentoDialog>;
}
